import { useState, useEffect, useRef, FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useChat } from '../../contexts/ChatContext';

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function ChatWidget() {
  const location = useLocation();
  const { userMessages, sendUserMessage, userUnreadCount, setUserName } = useChat();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [nameSet, setNameSet] = useState(() => {
    const stored = localStorage.getItem('sipdash_chat_name');
    return !!stored && stored !== 'Guest';
  });
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (open && !isAdmin) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    }
  }, [open, userMessages.length, isAdmin]);

  useEffect(() => {
    if (open && nameSet && !isAdmin) inputRef.current?.focus();
  }, [open, nameSet, isAdmin]);

  if (isAdmin) return null;

  const handleSend = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    sendUserMessage(text.trim());
    setText('');
  };

  const handleSetName = (e: FormEvent) => {
    e.preventDefault();
    if (!nameInput.trim()) return;
    setUserName(nameInput.trim());
    setNameSet(true);
    sendUserMessage(`Hi! I\'m ${nameInput.trim()}. I need some help.`);
  };

  return (
    <>
      {open && (
        <div
          className="fixed bottom-20 right-6 z-[200] w-80 bg-white rounded-2xl border border-stone-200 flex flex-col overflow-hidden"
          style={{ height: '460px', fontFamily: "'DM Sans', sans-serif" }}
        >
          <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

          {/* Header */}
          <div className="bg-orange-500 px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-goblet-fill text-white text-sm" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold leading-tight">SipDash Support</p>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-green-300 rounded-full inline-block" />
                  <span className="text-orange-100 text-xs">Online</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition cursor-pointer"
            >
              <i className="ri-close-line text-white text-sm" />
            </button>
          </div>

          {!nameSet ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 gap-4">
              <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center">
                <i className="ri-chat-smile-2-line text-orange-500 text-xl" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-stone-800">Welcome!</p>
                <p className="text-xs text-stone-500 mt-1">Tell us your name to get started.</p>
              </div>
              <form onSubmit={handleSetName} className="w-full space-y-2">
                <input
                  autoFocus
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Your name..."
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition"
                />
                <button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold py-2 rounded-lg transition cursor-pointer whitespace-nowrap"
                >
                  Start Chat
                </button>
              </form>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {userMessages.length === 0 && (
                  <div className="text-center py-6">
                    <p className="text-xs text-stone-400">Start the conversation below.</p>
                  </div>
                )}
                {userMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.sender === 'admin' && (
                      <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mr-2 mt-0.5 shrink-0">
                        <i className="ri-goblet-fill text-orange-500 text-xs" />
                      </div>
                    )}
                    <div
                      className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                        msg.sender === 'user'
                          ? 'bg-orange-500 text-white rounded-br-sm'
                          : 'bg-stone-100 text-stone-800 rounded-bl-sm'
                      }`}
                    >
                      <p>{msg.text}</p>
                      <p className={`text-[10px] mt-0.5 ${msg.sender === 'user' ? 'text-orange-200' : 'text-stone-400'}`}>
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <form
                onSubmit={handleSend}
                className="px-3 py-3 border-t border-stone-100 flex items-center gap-2 shrink-0"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-3 py-2 text-sm bg-stone-100 rounded-full outline-none focus:bg-stone-200 transition"
                />
                <button
                  type="submit"
                  disabled={!text.trim()}
                  className="w-9 h-9 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white rounded-full flex items-center justify-center transition cursor-pointer shrink-0"
                >
                  <i className="ri-send-plane-fill text-sm" />
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[200] w-14 h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition cursor-pointer"
        style={{ boxShadow: '0 4px 20px rgba(249,115,22,0.4)' }}
      >
        {open ? (
          <i className="ri-close-line text-xl" />
        ) : (
          <>
            <i className="ri-chat-1-fill text-xl" />
            {userUnreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {userUnreadCount}
              </span>
            )}
          </>
        )}
      </button>
    </>
  );
}
